import Image from 'next/image';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { signIn } from 'next-auth/react';
import { useEffect, useRef, useState } from 'react';
import { AUTH_PROVIDER } from '@/constants/auth';
import { Slide, useMediaQuery, useTheme } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useResolvedColorScheme } from '@/hooks/useResolvedColorScheme';
import { COLOR_SCHEME } from '@/constants/colorScheme';
import { useAdminGuard } from '@/hooks/useAdminGuard';
import { Box, Button, IconButton, Stack, styled, Toolbar } from '@/primitives';
import { UserAvatar } from '@/components/avatars/UserAvatar';
import {
  APP_ROUTE_NAMES,
  APP_ROUTE_PATHS,
  PUBLIC_ROUTES,
} from '@/configs/routes';
import { useIsPublicSearch } from '@/hooks/useIsPublicSearch';
import { useIsRegistryRoute } from '@/hooks/useIsRegistryRoute';
import { NAV_HEIGHT } from '@/configs/layout';
import SocietySearchHeaderCenterDesktop from '@/components/publicSearch/searchPage/SocietySearchHeaderCenterDesktop';
import SocietySearchHeaderLogo from '@/components/publicSearch/searchPage/SocietySearchHeaderLogo';
const HEADER_TRANSITION_MS = 280;

const LinkStyled = styled(Link)`
  display: flex;
  align-items: center;
  text-decoration: none;
`;

const navLinkStackSx = {
  flexDirection: { xs: 'column', md: 'row' },
  alignItems: { md: 'center' },
  gap: { xs: '6px', md: '8px' },
  width: { xs: '100%', md: 'fit-content' },
  '& .MuiButton-root': {
    textTransform: 'none',
    fontSize: { xs: '12px', md: '12px' },
    '&:hover': {
      backgroundColor: 'action.hover',
    },
  },
  a: {
    justifyContent: { xs: 'start', md: 'center' },
    padding: { xs: '4px 6px', md: '6px 16px' },
    minHeight: { xs: '30px', md: 0 },
    height: { xs: 'auto', md: 'auto' },
    width: 'fit-content',
    minWidth: 0,
    fontSize: { xs: '12px', md: '12px' },
    lineHeight: { xs: 1.3, md: 1.5 },
    fontWeight: 600,
  },
} as const;

interface Props {
  onNavigate?: () => void;
  onClose?: () => void;
}

function HeaderNavLinkStack({
  pathname,
  isPublicSearch,
  isRegistryRoute,
  onNavigate,
}: {
  pathname: string;
  isPublicSearch: boolean;
  isRegistryRoute: boolean;
  onNavigate?: () => void;
}) {
  return (
    <Stack direction="row" sx={navLinkStackSx}>
      <Button
        component={LinkStyled}
        href={PUBLIC_ROUTES.HOME}
        sx={{
          color:
            pathname === PUBLIC_ROUTES.HOME ? 'primary.main' : 'text.secondary',
        }}
        onClick={onNavigate}
      >
        {APP_ROUTE_NAMES.HOME}
      </Button>
      <Button
        component={LinkStyled}
        href={PUBLIC_ROUTES.SEARCH}
        sx={{
          color:
            pathname === PUBLIC_ROUTES.SEARCH || isPublicSearch
              ? 'primary.main'
              : 'text.secondary',
        }}
        onClick={onNavigate}
      >
        {APP_ROUTE_NAMES.SEARCH}
      </Button>
      <Button
        component={LinkStyled}
        href={PUBLIC_ROUTES.REGISTRY_LANDING}
        sx={{
          color: isRegistryRoute ? 'primary.main' : 'text.secondary',
        }}
        onClick={onNavigate}
      >
        {APP_ROUTE_NAMES.REGISTRY}
      </Button>
    </Stack>
  );
}

export default function Navigation({ onNavigate, onClose }: Props) {
  const themeMui = useTheme();
  const { resolvedScheme } = useResolvedColorScheme();
  const isMdUp = useMediaQuery(themeMui.breakpoints.up('md'));
  const isPublicSearch = useIsPublicSearch();
  const isRegistryRoute = useIsRegistryRoute();
  const pathname = usePathname();
  const { isAuthenticated } = useAdminGuard(APP_ROUTE_PATHS.DASHBOARD);

  const centerSlideKey = isPublicSearch
    ? 'header-center-society'
    : 'header-center-default';

  const breedlogicLogoSrc =
    resolvedScheme === COLOR_SCHEME.DARK
      ? '/breedlogic-logo-dark.svg'
      : '/breedlogic-logo.svg';
  const leftHeaderRef = useRef<HTMLDivElement>(null);
  const rightHeaderRef = useRef<HTMLDivElement>(null);
  const [desktopCenterOffsetPx, setDesktopCenterOffsetPx] = useState(0);

  useEffect(() => {
    if (!isMdUp) {
      setDesktopCenterOffsetPx(0);
      return;
    }

    const updateCenterOffset = () => {
      const leftWidth =
        leftHeaderRef.current?.getBoundingClientRect().width ?? 0;
      const rightWidth =
        rightHeaderRef.current?.getBoundingClientRect().width ?? 0;
      const nextOffset = (rightWidth - leftWidth) / 2;
      setDesktopCenterOffsetPx((prev) =>
        Math.abs(prev - nextOffset) < 0.5 ? prev : nextOffset,
      );
    };

    updateCenterOffset();
    const resizeObserver = new ResizeObserver(updateCenterOffset);
    if (leftHeaderRef.current) resizeObserver.observe(leftHeaderRef.current);
    if (rightHeaderRef.current) resizeObserver.observe(rightHeaderRef.current);
    window.addEventListener('resize', updateCenterOffset);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', updateCenterOffset);
    };
  }, [isMdUp, isPublicSearch, isAuthenticated]);

  return (
    <Toolbar
      component="nav"
      disableGutters
      sx={{
        flexDirection: { xs: 'column', md: 'row' },
        display: { xs: 'flex', md: 'grid' },
        gridTemplateColumns: { md: 'auto minmax(0, 1fr) auto' },
        alignItems: { xs: 'start', md: 'center' },
        columnGap: { md: '16px' },
        gap: { xs: '10px', md: 0 },
        px: { xs: 0, md: '24px' },
        py: { md: 0 },
        width: { md: '100%' },
        height: { md: '100%' },
        minHeight: { xs: 'auto', md: `${NAV_HEIGHT}px` },
        boxSizing: 'border-box',
      }}
    >
      {isMdUp ? (
        <Stack
          ref={leftHeaderRef}
          direction="row"
          alignItems="center"
          gap="12px"
          sx={{ flexShrink: 0, minWidth: 0 }}
        >
          <LinkStyled
            href={PUBLIC_ROUTES.HOME}
            sx={{
              display: 'flex',
              alignItems: 'center',
              flexShrink: 0,
              width: 112,
              height: 36,
              padding: 0,
            }}
          >
            <Image
              alt="BreedLOGIC Logo"
              height={37}
              width={176}
              priority
              src={breedlogicLogoSrc}
              style={{
                objectFit: 'contain',
                width: '100%',
                height: '100%',
              }}
            />
          </LinkStyled>
          {isPublicSearch ? <SocietySearchHeaderLogo /> : null}
        </Stack>
      ) : (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            width: '100%',
          }}
        >
          <LinkStyled
            href={PUBLIC_ROUTES.HOME}
            sx={{
              display: 'flex',
              alignItems: 'center',
              flexShrink: 0,
              width: 96,
              height: 30,
              padding: 0,
            }}
          >
            <Image
              alt="BreedLOGIC Logo"
              height={37}
              width={176}
              priority
              src={breedlogicLogoSrc}
              style={{
                objectFit: 'contain',
                width: '100%',
                height: '100%',
              }}
            />
          </LinkStyled>
          {onClose && (
            <IconButton size="small" onClick={onClose}>
              <CloseIcon sx={{ color: 'text.secondary', fontSize: 22 }} />
            </IconButton>
          )}
        </Box>
      )}

      {isMdUp ? (
        <Box
          sx={{
            minWidth: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            // When the left column (logo) is wider than the right column, translateX shifts
            // the center box left toward the logo. We add compensating left padding so the
            // tab content never visually crosses into the adjacent column, and so the
            // overflow measurement naturally uses the correct reduced budget.
            pl: `${4 + Math.max(0, -desktopCenterOffsetPx - 16 - 4)}px`,
            pr: `${4 + Math.max(0, desktopCenterOffsetPx - 16 - 4)}px`,
            overflow: 'hidden',
            transform: `translateX(${desktopCenterOffsetPx}px)`,
            transition: 'transform 180ms ease',
          }}
        >
          <Slide
            appear
            direction={isPublicSearch ? 'down' : 'up'}
            timeout={HEADER_TRANSITION_MS}
            in
            key={centerSlideKey}
          >
            <Box
              sx={{
                display: 'flex',
                width: '100%',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              {isPublicSearch ? (
                <SocietySearchHeaderCenterDesktop />
              ) : (
                <HeaderNavLinkStack
                  pathname={pathname}
                  isPublicSearch={isPublicSearch}
                  isRegistryRoute={isRegistryRoute}
                  onNavigate={onNavigate}
                />
              )}
            </Box>
          </Slide>
        </Box>
      ) : (
        <Slide
          appear
          direction={isPublicSearch ? 'down' : 'up'}
          timeout={HEADER_TRANSITION_MS}
          in
          key={
            isPublicSearch ? 'header-mobile-society' : 'header-mobile-default'
          }
        >
          <Box sx={{ width: '100%' }}>
            <HeaderNavLinkStack
              pathname={pathname}
              isPublicSearch={isPublicSearch}
              isRegistryRoute={isRegistryRoute}
              onNavigate={onNavigate}
            />
          </Box>
        </Slide>
      )}

      <Stack
        ref={rightHeaderRef}
        direction={{ xs: 'column', md: 'row' }}
        alignItems={{ xs: 'stretch', md: 'center' }}
        justifyContent={{ md: 'flex-end' }}
        gap={{ xs: 1, md: 1.5 }}
        sx={{
          flexShrink: 0,
          width: { xs: '100%', md: 'auto' },
        }}
      >
        {isAuthenticated ? (
          <Box
            sx={{
              display: 'flex',
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: '4px',
              padding: { xs: '4px 5px', md: '5px 6px' },
              width: { xs: '100%', md: 'auto' },
              minWidth: { xs: '100%', md: '160px' },
            }}
          >
            <UserAvatar compact inline={!isMdUp} />
          </Box>
        ) : (
          <Button
            sx={{
              minHeight: { xs: '32px', md: '40px' },
              height: { xs: '32px', md: '40px' },
              width: { xs: '100%', md: 'auto' },
              fontSize: { xs: '12px', md: '14px' },
              py: { xs: 0, md: undefined },
              textTransform: 'none',
            }}
            variant="contained"
            onClick={() => {
              void signIn(AUTH_PROVIDER, { callbackUrl: pathname });
              onNavigate?.();
            }}
          >
            Sign in
          </Button>
        )}
      </Stack>
    </Toolbar>
  );
}
